import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import {
  resetMeetingSummaryAgent,
  resetMeetingSummaryAgentDisplay,
  selectMeetingSummaryAgentAiAgentId,
  selectMeetingSummaryAgentAiAgentResultId,
  selectMeetingSummaryAgentEventsForPolling,
  setMeetingSummary,
  setMeetingSummaryAgentEventsForPolling,
  setMeetingSummaryAgentEventsForPollingLoading,
  setMeetingSummaryAgentLoading,
  setMeetingSummaryAiAgentModelLoading,
  setMeetingSummaryAiAgentStatus,
} from '../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { selectSelectedMeetingTemplate } from '../../../../../../store/meetingSummary/meetingTemplates'
import { AIAgentType } from '../../../../../../models/AIAgent'
import { useLazyGetAiAgentResultQuery } from '../../../../../../store/ai/aiAgentResult'
import { useLazyListRoomEventsQuery } from '../../../../../../store/media/media'
import { useLazyGetAiAgentQuery } from '../../../../../../store/ai/aiAgentModel'
import { setMessages } from '../../../../../../store/streamingChat/streamingChat'

export const MeetingSummaryAgentInitialization: React.FC = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)

  useInitializeExistingMeetingSummary(accountId, roomId, sessionId, selectedMeetingTemplate?.id)
  useInitializeMeetingSummaryEventsForPolling(accountId, roomId, sessionId)
  useInitializeMeetingSummaryAgentStatus(accountId)
  useCleanupMeetingSummary()

  return null
}

const useInitializeExistingMeetingSummary = (accountId: string, roomId: string, sessionId: string, meetingTemplateId?: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  useEffect(() => {
    const getExistingMeetingSummary = async () => {
      if (!meetingTemplateId) {
        return
      }
      try {
        const existingMeetingSummary = await triggerGetAiAgentResult(
          {
            accountId,
            type: AIAgentType.MEETING_SUMMARY,
            resultKey: `${roomId}|${sessionId}|${meetingTemplateId}`,
          },
          false,
        ).unwrap()
        if (existingMeetingSummary) {
          dispatch(setMeetingSummary(existingMeetingSummary))
          const { interaction } = existingMeetingSummary
          if (!interaction) {
            return
          }
          dispatch(
            setMessages(
              interaction.interactions.map((interaction) => {
                return { message: interaction.result.message, type: interaction.result.role }
              }),
            ),
          )
        }
      } finally {
        dispatch(setMeetingSummaryAgentLoading(false))
      }
    }
    getExistingMeetingSummary()
  }, [dispatch, accountId, roomId, sessionId, meetingTemplateId, triggerGetAiAgentResult])
}

const useInitializeMeetingSummaryEventsForPolling = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const eventsForPolling = useAppSelector(selectMeetingSummaryAgentEventsForPolling)

  const [triggerGetRoomEvents] = useLazyListRoomEventsQuery()

  useEffect(() => {
    const fetchMeetingSummaryEvents = async () => {
      if (eventsForPolling.length) {
        return
      }
      dispatch(setMeetingSummaryAgentEventsForPollingLoading(true))
      try {
        const { data: roomEvents } = await triggerGetRoomEvents(
          {
            accountId,
            roomId,
            sessionId,
            pageRequest: { page: 0, size: 500 },
            types: ['meetingSummaryAgentExecuted', 'aiFeatureExecutionFinished', 'userAllocationUpdated'],
          },
          true,
        )
        if (!roomEvents?.data || !roomEvents.data.length) {
          return
        }
        dispatch(setMeetingSummaryAgentEventsForPolling(roomEvents.data))
      } finally {
        dispatch(setMeetingSummaryAgentEventsForPollingLoading(false))
      }
    }
    fetchMeetingSummaryEvents()
  }, [dispatch, accountId, roomId, sessionId, triggerGetRoomEvents, eventsForPolling])
}

const useInitializeMeetingSummaryAgentStatus = (accountId: string) => {
  const dispatch = useAppDispatch()

  const meetingSummaryAgentId = useAppSelector(selectMeetingSummaryAgentAiAgentId)
  const meetingSummaryAiAgentResultId = useAppSelector(selectMeetingSummaryAgentAiAgentResultId)

  const [triggerGetAiAgent] = useLazyGetAiAgentQuery()

  useEffect(() => {
    const fetchMeetingSummaryAgent = async () => {
      if (!meetingSummaryAgentId || meetingSummaryAiAgentResultId) {
        return
      }
      dispatch(setMeetingSummaryAiAgentModelLoading(true))
      try {
        const aiAgent = await triggerGetAiAgent({
          accountId,
          aiAgentId: meetingSummaryAgentId,
        }).unwrap()
        if (!aiAgent) {
          return
        }
        dispatch(setMeetingSummaryAiAgentStatus(aiAgent.status))
      } finally {
        dispatch(setMeetingSummaryAiAgentModelLoading(false))
      }
    }
    fetchMeetingSummaryAgent()
  }, [dispatch, accountId, triggerGetAiAgent, meetingSummaryAgentId, meetingSummaryAiAgentResultId])
}

const useCleanupMeetingSummary = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetMeetingSummaryAgent())
      dispatch(resetMeetingSummaryAgentDisplay())
    }
  }, [dispatch])
}
